import { z } from 'zod'
// The artifact schemas live in the model layer (model/artifacts/artifacts.schema.ts — dependency-clean, so
// bundling contracts into the web stays safe) and are shared with untrusted-storage validation.
import { artifactsSchema } from '../../model/artifacts/artifacts.schema'

// ── Specials ─────────────────────────────────────────────────────────────────
// Every special carries a server-stamped `version` (never model-authored, PD-1713) so a
// type can be restructured later while stored legacy specials stay readable. Each strict
// schema pins its version as a literal, so a reader bundled with an older schema rejects a
// newer version and renders nothing — the same degrade as an unknown `type`. Evolution
// rule: an additive optional field keeps the version; renaming, removing or changing the
// meaning of a field bumps it, and the old schema stays for the stored specials that
// carry it. `followup-visit-topics` predates versioning: an absent version means 1.
export const FOLLOWUP_VISIT_TOPICS_VERSION = 1
export const RISKS_GAPS_FINDINGS_VERSION = 1

// Full strict schema of the `followup-visit-topics` special (PD-1379) — the
// shape the envelope parser enforces on model output, and the narrowing guard
// web uses before rendering. `id` is stamped by the envelope parser from the
// topic's position (topics ride most-critical-first), never model-authored.
// The category vocabulary lives HERE (this file must stay import-clean for
// the web bundle); `assistant/types.ts` derives its type from this const.
export const FOLLOWUP_VISIT_TOPIC_CATEGORIES = [
  'data-integrity',
  'safety-alert',
  'risk',
  'care-gap',
  'monitoring',
  'documentation',
] as const

// One piece of evidence behind a topic. Structured (not markdown prose) per
// the Visit Guidance spec: pill shows label/value/date, expanding reveals
// `detail`; `href` (optional) links the source — a chart document/section or a
// guideline document url, in either case only ones the model's inputs carried.
export const followupVisitTopicEvidenceSchema = z.object({
  label: z.string(),
  value: z.string(),
  date: z.string().optional(),
  detail: z.string().optional(),
  // Internal path or http(s) only — anything else (javascript:, data:,
  // protocol-relative //host, …) must never reach a clickable anchor. Enforced
  // here so the envelope retries bad model output AND web's narrowing rejects
  // bad stored values.
  href: z
    .string()
    .regex(/^(\/(?!\/)|https?:\/\/)/i, 'href must be an internal path or an http(s) URL')
    .optional(),
})

export const followupVisitTopicSchema = z.object({
  id: z.number(),
  priority: z.enum(['critical', 'high', 'medium', 'low']),
  // 1-3 per the prompt contract — enforced so out-of-contract model output
  // triggers the envelope retry instead of reaching clinicians.
  categories: z.array(z.enum(FOLLOWUP_VISIT_TOPIC_CATEGORIES)).min(1).max(3),
  title: z.string(),
  // One natural, patient-friendly question the practitioner can ask directly.
  question: z.string(),
  // Why the question is relevant — specific evidence with dates, most recent first.
  basedOn: z.string(),
  // 1-3 per the prompt contract (a topic without evidence must not exist).
  evidence: z.array(followupVisitTopicEvidenceSchema).min(1).max(3),
  // The registry measure of the Risks & Gaps finding the topic derives from (PD-1713);
  // absent when the topic maps to no registry measure.
  measureSlug: z.string().optional(),
})

/**
 * A Visit Guidance topic's anchor on the page, from its title: a rebuild renumbers the topics
 * (`id` is the position), so a link by id could land on another one. Shared by the daily
 * briefing's link and the topic card (FNV-1a, browser-safe).
 */
export function visitTopicAnchor(title: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < title.length; i++) {
    hash ^= title.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return `visit-topic-${(hash >>> 0).toString(36)}`
}

export const followupVisitTopicsSpecialSchema = z.object({
  specialId: z.string(),
  type: z.literal('followup-visit-topics'),
  // Optional: specials stored before versioning carry none (= 1).
  version: z.literal(FOLLOWUP_VISIT_TOPICS_VERSION).optional(),
  // Up to 5 per the prompt contract (empty = explicit "nothing qualifies").
  topics: z.array(followupVisitTopicSchema).max(5),
})
export type FollowupVisitTopicsSpecialWire = z.infer<typeof followupVisitTopicsSpecialSchema>
export type FollowupVisitTopicWire = z.infer<typeof followupVisitTopicSchema>

// Strict schema of the `risks-gaps-findings` special (PD-1713, docs/cpg/RISKS_GAPS.md): the
// Risks & Gaps report's rows, each tied to a registry measure or carrying a proposed one.
// It is the ONLY form the specialist authors the rows in — the server validates it against
// the measure registry, enriches resolved findings with their family/group, stamps the ids
// and the version, and derives the report's two seven-column tables from it for the
// consumers that still read tables.
export const RISKS_GAPS_FINDING_KINDS = ['risk', 'gap'] as const
export const RISKS_GAPS_FINDING_GRADES = ['high', 'medium'] as const
export const RISKS_GAPS_CONFIDENCE_LABELS = ['High', 'Medium', 'Low'] as const

// A measure the agent proposes when no registry measure fits the finding. Stored as the
// agent wrote it; nothing reaches the registry at build time (PD-1714 accumulates, PD-1716
// clusters). `guidelineUnitIds` are the cpg_unit passages it was grounded in, filtered by
// the server to the ones that run actually surfaced.
export const risksGapsProposedMeasureSchema = z.object({
  slug: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'slug must be kebab-case'),
  label: z.string().min(1),
  kind: z.enum(RISKS_GAPS_FINDING_KINDS),
  familySlug: z.string(),
  definition: z.string().min(1),
  gradingRule: z.string().min(1),
  rationale: z.string().min(1),
  guidelineUnitIds: z.array(z.number().int()).optional(),
})

// The authored content of one finding — the seven report columns minus RISK CATEGORY,
// which is the measure's (or proposal's) label.
export const risksGapsFindingContentSchema = z.object({
  kind: z.enum(RISKS_GAPS_FINDING_KINDS),
  grade: z.enum(RISKS_GAPS_FINDING_GRADES),
  finding: z.string(),
  chartEvidence: z.string(),
  whyItMatters: z.string(),
  recommendedNextStep: z.string(),
  confidence: z.enum(RISKS_GAPS_CONFIDENCE_LABELS),
  missingData: z.string(),
})

// `id` is the finding's position in `findings`, stamped by the server — the stable per-row
// key (PD-1714). A resolved finding carries its measure's registry labels, so consumers
// lay out family group → family without a registry read.
export const risksGapsFindingSchema = z.union([
  risksGapsFindingContentSchema.extend({
    id: z.number(),
    measureSlug: z.string(),
    measureLabel: z.string(),
    familySlug: z.string(),
    familyLabel: z.string(),
    groupSlug: z.string(),
    groupLabel: z.string(),
  }),
  risksGapsFindingContentSchema.extend({
    id: z.number(),
    proposedMeasure: risksGapsProposedMeasureSchema,
  }),
])

export const risksGapsFindingsSpecialSchema = z.object({
  specialId: z.string(),
  type: z.literal('risks-gaps-findings'),
  // Required: the type was born versioned.
  version: z.literal(RISKS_GAPS_FINDINGS_VERSION),
  findings: z.array(risksGapsFindingSchema),
})
export type RisksGapsProposedMeasureWire = z.infer<typeof risksGapsProposedMeasureSchema>
export type RisksGapsFindingWire = z.infer<typeof risksGapsFindingSchema>
export type RisksGapsFindingsSpecialWire = z.infer<typeof risksGapsFindingsSpecialSchema>

// The loose WIRE shape of a special (`type` + passthrough) lives with the other artifact
// schemas in model/artifacts/artifacts.schema.ts (`artifactSpecialSchema`, on `artifactsSchema`);
// the strict per-type schemas above are what consumers apply when narrowing.

const entryBaseShape = { id: z.string(), createdAt: z.string() }

// Token usage for a turn (and, rolled up, for a whole conversation). `costUsd` is
// the summed list-price estimate, present only when the model was priced. Debug-only
// on the wire — the non-debug projection omits it. Shared by the per-turn entry shape
// here and the conversation-level field in assistant-conversation.contract.
export const chatUsageSchema = z
  .object({
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadInputTokens: z.number().optional(),
    cacheCreationInputTokens: z.number().optional(),
    costUsd: z.number().optional(),
  })
  .strict()

// Terminal answer of a turn. `response` (raw model JSON) is debug-only on the
// wire — the projection strips it outside debug mode. `artifacts` groups the
// renderable outputs (including the prose `answer`) and is omitted entirely when
// the turn produced none. `error` (the validator's failure description) is only
// ever populated alongside `status: 'error'`, once retries are exhausted — a
// `retry: true` result (see BuildAssistantEntryResult in types.ts) is an
// internal Agent.process signal intercepted before any entry is persisted.
export const threadEntryAssistantSchema = z.object({
  response: z.string().optional(),
  status: z.enum(['complete', 'error', 'cancelled']),
  fallback: z.literal(true).optional(),
  error: z.string().optional(),
  artifacts: artifactsSchema.optional(),
})

// One processing step. Discriminated on `type`; the thinking variant further
// splits on `subtype` (model = signed, replayed to the provider; tool = UI-only
// progress). Model-thinking carries `signature`/`data`/`contentBlockIndex`;
// tool-thinking omits them, so they're optional on the unified thinking shape.
const processingThinkingSchema = z.object({
  ...entryBaseShape,
  type: z.literal('thinking'),
  subtype: z.enum(['model', 'tool']),
  text: z.string(),
  signature: z.string().optional(),
  data: z.string().optional(),
  toolTurnId: z.string(),
  contentBlockIndex: z.number().int().nonnegative().optional(),
})

export const threadEntryProcessingSchema = z.discriminatedUnion('type', [
  processingThinkingSchema,
  z.object({
    ...entryBaseShape,
    type: z.literal('tool_use'),
    toolUseId: z.string(),
    name: z.string(),
    input: z.unknown(),
    toolTurnId: z.string(),
  }),
  z.object({
    ...entryBaseShape,
    type: z.literal('tool_result'),
    toolUseId: z.string(),
    // The tool's structured ToolResponse. `status`/`error`/`threadId`/`artifacts`
    // are typed; the tool's own data fields pass through (loose) so the debug
    // Result view shows the full payload rather than an empty object.
    result: z.looseObject({
      status: z.enum(['complete', 'error', 'cancelled']),
      error: z.string().optional(),
      threadId: z.string().optional(),
      artifacts: artifactsSchema.optional(),
    }),
    isError: z.boolean().optional(),
  }),
  z.object({
    createdAt: z.string(),
    type: z.literal('retry'),
    // The rejected attempt verbatim — replayed to the model on the next call as
    // its own prior turn, followed by `assistant.error` as corrective feedback
    // (see buildLlmMessages in agent.ts). No `id`: unlike other processing
    // steps, a retry step is never looked up or upserted by id.
    assistant: threadEntryAssistantSchema,
  }),
])

const systemEntrySchema = z.object({
  ...entryBaseShape,
  kind: z.literal('system'),
  system: z.object({ text: z.string() }),
})

const turnEntrySchema = z.object({
  ...entryBaseShape,
  kind: z.literal('turn'),
  user: z.object({ text: z.string() }).optional(),
  processing: z.array(threadEntryProcessingSchema).optional(),
  assistant: threadEntryAssistantSchema.optional(),
  // Total token usage for the turn (debug-only on the wire). Stripped in non-debug
  // by the projection building a fresh entry without it.
  usage: chatUsageSchema.optional(),
})

export const threadEntrySchema = z.discriminatedUnion('kind', [systemEntrySchema, turnEntrySchema])
